import React, { useState, useEffect } from 'react';
import {
        Box,
        Card,
        Typography,
        Button,
        Table,
        TableBody,
        TableCell,
        TableContainer,
        TableHead,
        TableRow,
        Chip,
        Grid,
        useTheme,
        LinearProgress,
        alpha,
} from '@mui/material';
import {
        AutoMode,
        CheckCircle,
        Warning,
        TrendingUp,
} from '@mui/icons-material';
import LoadingState from '../../components/common/LoadingState';
import ErrorAlert from '../../components/common/ErrorAlert';
import EmptyState from '../../components/common/EmptyState';
import { simpleApi, ReorderSuggestion } from '../../services/simpleApi';

interface ReorderSuggestionDisplay {
	product_id: string;
	product_name: string;
	current_inventory: number;
	reorder_point: number;
	suggested_quantity: number;
	supplier_id: string;
	reason: string;
}

const ReorderingPage: React.FC = () => {
	const theme = useTheme();
	const [suggestions, setSuggestions] = useState<ReorderSuggestionDisplay[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		fetchSuggestions();
	}, []);

	const fetchSuggestions = async () => {
		try {
			setLoading(true);
			const response = await simpleApi.reordering.getSuggestions();
			const items: ReorderSuggestionDisplay[] = response.map((s: ReorderSuggestion) => ({
				product_id: s.product_id,
				product_name: s.product_name,
				current_inventory: s.current_stock,
				reorder_point: s.reorder_point,
				suggested_quantity: s.suggested_quantity,
				supplier_id: s.supplier_id || 'N/A',
				reason: s.reason,
			}));
			setSuggestions(items);
			setError(null);
		} catch (err: any) {
			setError(err.message || 'Failed to load suggestions');
		} finally {
			setLoading(false);
		}
	};        return (
                <Box>
                        {/* Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                                <Box>
                                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                                                Smart Reordering
                                        </Typography>
                                        <Typography variant="body1" color="text.secondary">
                                                AI-driven replenishment suggestions and automation rules
                                        </Typography>
                                </Box>
                                <Button variant="contained" startIcon={<AutoMode />}>
                                        Configure Rules
                                </Button>
                        </Box>

                        {error && <ErrorAlert error={error} onRetry={fetchSuggestions} />}

                        {/* Suggestions Table */}
                        <Card>
                                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                Replenishment Suggestions
                                        </Typography>
                                </Box>

                                {loading ? (
                                        <LoadingState message="Analyzing inventory levels..." />
                                ) : suggestions.length === 0 ? (
                                        <EmptyState
                                                title="No reorders needed"
                                                description="Inventory levels are healthy across all locations"
                                                actionLabel="Check Rules"
                                                icon={<CheckCircle sx={{ fontSize: 64, color: 'success.main' }} />}
                                        />
                                ) : (
                                        <TableContainer>
                                                <Table>
                                                        <TableHead>
                                                                <TableRow>
                                                                        <TableCell>Product</TableCell>
                                                                        <TableCell>Current Stock</TableCell>
                                                                        <TableCell>Reorder Point</TableCell>
                                                                        <TableCell>Suggested Qty</TableCell>
                                                                        <TableCell>Reason</TableCell>
                                                                        <TableCell align="right">Actions</TableCell>
                                                                </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                                {suggestions.map((item, index) => (
                                                                        <TableRow key={index} hover>
                                                                                <TableCell>
                                                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                                                {item.product_name}
                                                                                        </Typography>
                                                                                        <Typography variant="caption" color="text.secondary">
                                                                                                ID: {item.product_id}
                                                                                        </Typography>
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                        <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
                                                                                                {item.current_inventory}
                                                                                        </Typography>
                                                                                </TableCell>
                                                                                <TableCell>{item.reorder_point}</TableCell>
                                                                                <TableCell>
                                                                                        <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
                                                                                                {item.suggested_quantity}
                                                                                        </Typography>
                                                                                </TableCell>
                                                                                <TableCell>
                                                                                        <Chip
                                                                                                label={item.reason.replace('_', ' ')}
                                                                                                size="small"
                                                                                                color={item.reason === 'low_stock' ? 'warning' : 'info'}
                                                                                                icon={item.reason === 'low_stock' ? <Warning /> : <TrendingUp />}
                                                                                        />
                                                                                </TableCell>
                                                                                <TableCell align="right">
                                                                                        <Button variant="contained" size="small" sx={{ mr: 1 }}>
                                                                                                Approve
                                                                                        </Button>
                                                                                        <Button size="small" color="inherit">
                                                                                                Ignore
                                                                                        </Button>
                                                                                </TableCell>
                                                                        </TableRow>
                                                                ))}
                                                        </TableBody>
                                                </Table>
                                        </TableContainer>
                                )}
                        </Card>
                </Box>
        );
};

export default ReorderingPage;
